import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useState } from "react";

import { IDraftMessage } from "../../../store/queries/contractModelDrafts";
import { StepId, WIZARD_STEPS } from "./steps";

const stepTitle = (stepId: string | null): string | null =>
  WIZARD_STEPS.find((s) => s.id === stepId)?.title ?? null;

interface IProps {
  stepId: StepId;
  messages: IDraftMessage[];
  busy: boolean;
  onAdd: (body: string, stepId: StepId) => Promise<void>;
  onDelete: (messageId: string) => void;
}

/**
 * Where the reviewer tells the agent what to do differently.
 *
 * Two deliberate design choices, both of which look like omissions until you know why:
 *
 * 1. **This is not a chat.** No speech bubbles, no avatars — it is a numbered list of instructions.
 *    The agent CANNOT reply in real time (generation is a multi-minute batch job, and a conversational
 *    reply loop is blocked on a pending API decision). A chat UI would promise a reply that never
 *    comes, and the fastest way to make this feel broken is to imply it converses.
 *
 * 2. **Writing an instruction does not start a generation.** They queue. She usually has several
 *    things to say before she wants the model re-derived, and firing a minutes-long job on every
 *    keystroke-ful of feedback would be both expensive and maddening. She presses Regenerate when she
 *    has finished talking — which is also how she works with a chat LLM today.
 */
function InstructionPanel({ stepId, messages, busy, onAdd, onDelete }: IProps) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onAdd(text, stepId);
      setBody("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card border-round p-3 mt-4 border-1 surface-border">
      <div className="font-semibold mb-1">Tell the agent what to change</div>

      {messages.length > 0 && (
        <ol className="mt-2 mb-3 pl-3 text-sm">
          {messages.map((m) => (
            <li key={m.id} className="mb-2">
              <span
                className={m.appliedInVersionId ? "text-color-secondary" : ""}
              >
                {m.body}
              </span>
              <div className="flex align-items-center gap-2 mt-1">
                {m.stepId && m.stepId !== stepId ? (
                  <small className="text-color-secondary">
                    written on {stepTitle(m.stepId) ?? m.stepId}
                  </small>
                ) : null}
                {m.appliedInVersionId ? (
                  <small className="text-color-secondary">
                    <i
                      className="pi pi-check mr-1"
                      style={{ fontSize: "0.7rem" }}
                    />
                    applied
                  </small>
                ) : (
                  <>
                    <small className="text-orange-600">not yet applied</small>
                    <Button
                      label="Remove"
                      className="p-button-text p-button-sm p-0"
                      disabled={busy}
                      onClick={() => onDelete(m.id)}
                    />
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      <InputTextarea
        className="w-full"
        rows={2}
        autoResize
        placeholder="e.g. Treat the office column as a separate entity. The psych codes follow the 2024 grid, not the amendment."
        value={body}
        disabled={busy || saving}
        onChange={(e) => setBody(e.target.value)}
      />

      <div className="flex justify-content-between align-items-center mt-2">
        {/* The entire user model, in one sentence. It is honest: we do NOT claim the re-derive is
            scoped to this step, only that the change usually lands here. Narrowness is an outcome,
            not a promise — and pretending otherwise is how you ambush someone. */}
        <small className="text-color-secondary">
          Instructions apply to the whole model — the agent re-reads everything.
          Most changes will land here, but it may correct other steps too.
        </small>
        <Button
          label="Add instruction"
          icon="pi pi-plus"
          className="p-button-sm p-button-outlined"
          disabled={busy || saving || body.trim().length === 0}
          onClick={submit}
        />
      </div>
    </div>
  );
}

export default InstructionPanel;
